import React from 'react'

import {
  Box,
  Flex,
  Menu,
  MenuItem,
  MenuList,
  Text,
  MenuButton,
  IconButton,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Stack,
  ModalCloseButton,
} from '@chakra-ui/react'
import { Menu as MenuIcon } from 'src/components/icon'
import { useOpenModal } from 'src/hooks/use-open-modal'
import { useOpenPullDown } from 'src/hooks/use-open-pull-down'
import { useBreadcrumb } from 'src/hooks/useBreadcrumb'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'

import { IcfDocumentCardRenewal } from '../components/document-card-renewal'
import { CreateIcfDocumentRevisionItem, IcfDocumentDetail } from '../entity'
import { IcfDocumentErrors } from '../validate'

type Props = {
  icfDocument: IcfDocumentDetail | undefined
  nextRevision: CreateIcfDocumentRevisionItem | undefined
  filePreviewUrl: string
  listPath: string
  editPath: string
  errors: IcfDocumentErrors
  shouldSelectHospital: boolean
  hospitalName: string
  hasMultipleRevisionHistories: boolean
  hasTempFile: boolean
  pullDownState: ReturnType<typeof useOpenPullDown>
  submitModalState: ReturnType<typeof useOpenModal>
  onValidate: () => boolean
  onChangeName: (value: string) => void
  onChangeVersion: (value: string) => void
  onChangeRequiredPatientSign: () => void
  onSelectFile: (file: File) => Promise<void>
  onSubmit: () => Promise<void>
  onCancel: () => void
  openRevisionHistoryPane: () => void
}

export const AddIcfDocumentRevisionRenewal: React.FC<Props> = ({
  icfDocument,
  nextRevision,
  filePreviewUrl,
  listPath,
  editPath,
  errors,
  shouldSelectHospital,
  hospitalName,
  hasMultipleRevisionHistories,
  hasTempFile,
  submitModalState,
  onValidate,
  onChangeName,
  onChangeVersion,
  onChangeRequiredPatientSign,
  onSelectFile,
  onSubmit,
  onCancel,
  openRevisionHistoryPane,
}) => {
  const { renderBreadcrumb } = useBreadcrumb()

  if (!icfDocument || !nextRevision) {
    return null
  }

  return (
    <Flex h="full" direction="column" py="6">
      <Modal
        isOpen={submitModalState.modalOpen}
        onClose={submitModalState.handlers.closeModal}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>改版登録</ModalHeader>
          <ModalBody>文書の改版を登録します。</ModalBody>
          <ModalFooter>
            <ModalCancelButton />
            <Button type="submit" onClick={onSubmit}>
              改版登録
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Stack spacing="8" px="6">
        <Flex justify="space-between" wrap="wrap" rowGap={2}>
          {renderBreadcrumb([
            { text: '文書一覧', link: listPath },
            { text: '文書編集', link: editPath },
            { text: '改版登録', isCurrentPage: true },
          ])}
          <Flex justify="end" gap="4">
            <Button
              onClick={() => {
                if (!onValidate()) {
                  return
                }
                submitModalState.handlers.openModal()
              }}
            >
              改版登録
            </Button>
            <Menu>
              <MenuButton
                as={IconButton}
                variant="customIconButtonGhost"
                icon={<MenuIcon />}
              />
              <MenuList>
                <MenuItem onClick={onCancel} color="red.500">
                  改版を取り消し
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        {shouldSelectHospital && (
          <Text fontSize="sm" fontWeight="bold">
            医療機関 : {hospitalName}
          </Text>
        )}
      </Stack>

      <Box flex="1" w="full" overflow="auto" mt="8" pt="4" px="6">
        <Box minW="640px" maxW="780px">
          <IcfDocumentCardRenewal
            cardType="addRevision"
            numberingId={icfDocument.numberingId}
            name={nextRevision.name}
            documentType={nextRevision.type}
            version={nextRevision.version}
            fileName={nextRevision.fileName}
            filePreviewUrl={filePreviewUrl}
            isRequiredPatientSign={nextRevision.isRequiredPatientSign}
            onChangeName={onChangeName}
            onChangeVersion={onChangeVersion}
            onChangeRequiredPatientSign={onChangeRequiredPatientSign}
            onSelectFile={onSelectFile}
            hasTempFile={hasTempFile}
            errors={errors}
            hasMultipleRevisionHistories={hasMultipleRevisionHistories}
            openRevisionHistory={openRevisionHistoryPane}
          />
        </Box>
      </Box>
    </Flex>
  )
}
